import React, { FormEvent, useState, useEffect } from 'react';
import styled from 'App/styled';
import { useTheme } from 'emotion-theming';
import { NotifyMeParams } from 'store/notifyMe/types';
import { SUCCESS_MESSAGE, FAILURE_MESSAGE } from 'store/notifyMe/constants';
import Snackbar from 'components/Snackbar';
import { validateEmail } from 'shared/helpers/utils/validation';
import { PrimaryCategories, Product } from 'store/product/types';
import { pushNotifyMe } from 'shared/store/notifyMe/helpers';
import { isRegisteredViaMobile } from 'shared/store/auth/helper';
import { FormattedMessage, useStringFromId } from '@nykaa/intl-shared';
import { NOTIFY_ME, NOTIFY_STOCK, YOUR_EMAIL } from 'constants/strings';
import SocialNudge from 'layouts/Hlp/HybridProductCard/SocialNudge';

const SNACKBAR_ID = 'notify-me-prompt';
const INVALID_EMAIL_MESSAGE = 'Please enter valid email';

interface FormProps {
  borderColor: string;
  textColor: string;
  placeholderColor: string;
  submitTextColor: string;
}

interface WrapperProps {
  textColor: string;
  isBottomSheet: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  position: sticky;
  width: 100%;
  bottom: 0px;
  background: #fff;
  z-index: 2;
  ${({ theme }) => theme.typography.bodyMedium};
  color: ${({ textColor }) => textColor};
  ${({ theme, isBottomSheet }) =>
    !isBottomSheet &&
    `
    padding: ${theme.spacing.spacing100};
    box-shadow: 1px -1px 1px rgba(0, 0, 0, 0.2);
  `};
  .error-message {
    ${({ theme }) => theme.typography.bodyMedium};
    width: 100%;
    color: red;
  }
`;

const Form = styled.form`
  border: 1px solid ${({ borderColor }: FormProps) => borderColor};
  display: flex;
  padding: 6px 12px;
  color: ${({ textColor }: FormProps) => textColor};
  border-radius: 3px;

  .input {
    display: inline-block;
    border: none;
    flex-grow: 1;

    ::placeholder {
      color: ${({ placeholderColor }: FormProps) => placeholderColor};
      font-weight: 500;
    }

    &:focus {
      outline: none;
    }
  }

  .submit-button {
    color: ${({ submitTextColor }: FormProps) => submitTextColor};
    border: none;
    background: transparent;
    ${({ theme }) => theme.typography.buttonLarge};
  }
`;

interface NotifyMeProps {
  sendNotifyMe: (params: NotifyMeParams) => Promise;
  productId: string;
  userEmail: string;
  brandName: string;
  offerPrice: number;
  productName: string;
  primaryCategories?: PrimaryCategories;
  isLux: number;
  onNotifyClick?: (arg1: boolean, arg2: string) => void;
  isBottomSheet?: boolean;
  trackingData?: string;
  pageSource?: string;
  product?: Product;
  variantType?: string;
}

function NotifyMe({
  sendNotifyMe,
  offerPrice,
  userEmail,
  brandName,
  productId,
  productName,
  primaryCategories,
  isLux,
  onNotifyClick = () => {},
  isBottomSheet = false,
  trackingData,
  pageSource,
  product,
  variantType,
}: NotifyMeProps) {
  const [email, setEmail] = useState('');
  const [showNotifyMessage, setNotifyMeMessage] = useState(false);
  const [notifyMeError, setNotifyMeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const yourEmail = useStringFromId(YOUR_EMAIL);
  const theme: Theme = useTheme();
  useEffect(() => {
    !isRegisteredViaMobile(userEmail) && setEmail(userEmail);
  }, [userEmail]);
  const handleFormSubmit = (formSubmitEvent: FormEvent) => {
    formSubmitEvent.preventDefault();
    const validEmail = validateEmail(email);
    if (validEmail) {
      sendNotifyMe({
        email,
        productId,
        variantName: productName,
        offerPrice,
        brandName,
      }).then((res: boolean) => {
        const { childId, discount, mrp, packSize } = product;
        pushNotifyMe({
          product,
          productId,
          productName,
          offerPrice,
          brandName,
          primaryCategories,
          isLux,
          trackingData,
          pageSource,
          childId,
          discount,
          mrp,
          packSize,
          variantType,
        });
        setNotifyMeMessage(true);
        onNotifyClick(!res, res ? SUCCESS_MESSAGE : FAILURE_MESSAGE);
        setNotifyMeError(!res);
      });
    } else {
      setErrorMessage(INVALID_EMAIL_MESSAGE);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
    setErrorMessage('');
  };

  const formProps: FormProps = {
    borderColor: theme.lightGrey,
    placeholderColor: theme.lightGrey,
    submitTextColor: theme.brandPrimary,
    textColor: theme.darkGrey,
  };

  return (
    <Wrapper
      textColor={theme.darkGrey}
      isBottomSheet={isBottomSheet}
    >
      <SocialNudge
        product={product}
        areCornersRounded={true}
        addMarginBottom={false}
      />
      <span>
        <FormattedMessage id={NOTIFY_STOCK} />
      </span>
      <Form
        onSubmit={handleFormSubmit}
        {...formProps}
      >
        <input
          type="text"
          className="input"
          placeholder={yourEmail}
          value={email}
          onChange={handleChange}
          name="email"
          aria-label="Your Email"
        />
        <button
          type="submit"
          className="submit-button"
          onClick={handleFormSubmit}
        >
          <FormattedMessage id={NOTIFY_ME} />
        </button>
      </Form>
      {<span className="error-message">{errorMessage}</span>}
      {showNotifyMessage && (
        <Snackbar
          autoClose
          closeCallback={() => setNotifyMeMessage(false)}
          content={notifyMeError ? FAILURE_MESSAGE : SUCCESS_MESSAGE}
          errorType={notifyMeError}
          snackBarId={SNACKBAR_ID}
        />
      )}
    </Wrapper>
  );
}

export default NotifyMe;
